﻿import { apiCall } from "./utils";



var DEFAULT_STATE = {
    games: 0,
    wins: 0,
    fastestTime: null as number,
    averageTime: null as number,
};

export type PersonalStatsState = typeof DEFAULT_STATE;

export type FormattedStats = {
    games: number,
    wins: number,
    winPercentage: string,
    fastestTimeFormatted: string,
    averageTimeFormatted: string,

}

function padZero(num: number) {
    return num < 10 ? `0${num}` : num;
}   

const  GAME_KEYS: GameKeyEnum[] =[
    'Easy'
    , 'Medium'
    , 'Hard'
    , 'Tough'
    , 'Easy 4x4'
    , 'Hard 4x4'
    , 'Easy 6x6'
    , 'Hard 6x6'
    , 'Evil'
    , '16x16'
    , 'Parents'];

const prefix = 'PERSONAL_STATS_';

function getKey(gameKey: GameKeyEnum) {
    return `${prefix}${gameKey}`
}


var personalStatsLocal = {
    cloneDefaultState: function () {
        return JSON.parse(JSON.stringify(DEFAULT_STATE));
    },

    setStats: function (gameKey: GameKeyEnum, stats: any) {
        try {
            localStorage.setItem(getKey(gameKey), JSON.stringify(stats));
        } catch (e) {
            console.log(e)
        }
    },

    clear(gameKey: GameKeyEnum) {
        try {
            localStorage.removeItem(getKey(gameKey))
        } catch(e) {
            console.log(e)
        }
    },

    /**
     * Check if stats exists for a game
     */
    hasStats: function (gameKey: GameKeyEnum) {
        return !!localStorage.getItem(getKey(gameKey));
    },

    /**
     * Get stats for a game, if not found return default state
     */
    getStats: function (gameKey: GameKeyEnum): PersonalStatsState {
        let result;
        const defaultState = this.cloneDefaultState();
        try {
            const stats = localStorage.getItem(getKey(gameKey));
            if (!stats) {
                return defaultState;
            }
            result = JSON.parse(stats);
            // merge with default state.
            // This handles the situation where new stats are added to the default state
            return Object.assign({}, defaultState, result);
        } catch (e) {
            console.log(e);
        }
        return defaultState;
    },
    startGame: function (gameKey: GameKeyEnum) {
        let stats = this.getStats(gameKey);
        stats.games++;
        this.setStats(gameKey, stats);
    },
    winGame: function (gameKey: GameKeyEnum, timeInSeconds: number) {
        let stats = this.getStats(gameKey);

        if (stats.fastestTime === null) {
            stats.fastestTime = timeInSeconds;
        } else {
            stats.fastestTime = Math.min(stats.fastestTime, timeInSeconds);
        }

        if (stats.averageTime === null) {
            stats.averageTime = timeInSeconds
        } else {
            stats.averageTime = (stats.averageTime * stats.wins + timeInSeconds) / (stats.wins + 1)
        }
        stats.wins++;
        this.setStats(gameKey, stats);
    }
};

const personsalStatsServer = {
    startGame: function (gameName): Promise<void> {
        return apiCall(`/api/PersonalStatsStartGame`, {
            gameName
        });
    },
    winGame: function (gameName, timeInSeconds) {
        return apiCall(`/api/PersonalStatsWinGame`, {
            gameName,
            timeInSeconds
        });
    },
    setStats: function (gameName, stats) {
        return apiCall(`/api/PersonalStatsSetStats`, {
            gameName,
            games: stats.games,
            wins: stats.wins,
            fastestTime: stats.fastestTime,
            averageTime: stats.averageTime,
        });
    },
    getStats: async function (gameName): Promise<PersonalStatsState> {
        const result = await  apiCall(`/api/PersonalStatsGetStats`, {
            gameName
        }) as PersonalStatsState;
        return result;
    },
    clear: function (gameName) {
        return apiCall(`/api/PersonalStatsClearStats`, {
            gameName
        });
    }
}
class PersonalStats {
    isLoggedIn: boolean;
    constructor(loggedIn: boolean) {
        this.isLoggedIn = loggedIn;
    }

    getStats(gameKey: GameKeyEnum): Promise<PersonalStatsState> {
        if (this.isLoggedIn) {
            return personsalStatsServer.getStats(gameKey);
        } else {
            return Promise.resolve(personalStatsLocal.getStats(gameKey));
        }
    }

    getFormattedStats(gameKey: GameKeyEnum): Promise<FormattedStats> {
        return this.getStats(gameKey).then(stats => {
            return this.formatPersonalStats(stats);
        }
        );
    }

    startGame(gameKey: GameKeyEnum): Promise<void> {
        if (this.isLoggedIn) {
            return personsalStatsServer.startGame(gameKey);
        } else {
            return Promise.resolve(personalStatsLocal.startGame(gameKey));
        }
    }

    winGame(gameKey: GameKeyEnum, timeInSeconds: number): Promise<any> {
        if (this.isLoggedIn) {
            return personsalStatsServer.winGame(gameKey, timeInSeconds);
        } else {
            return Promise.resolve(personalStatsLocal.winGame(gameKey, timeInSeconds));
        }
    }

    clear(gameKey: GameKeyEnum){
        if (this.isLoggedIn) {
            return personsalStatsServer.clear(gameKey);
        } else {
            return personalStatsLocal.clear(gameKey);
        }
    }

    setStats(gameKey: GameKeyEnum, stats: any): Promise<any> {
        if (this.isLoggedIn) {
            return personsalStatsServer.setStats(gameKey, stats);
        } else {
            return Promise.resolve(personalStatsLocal.setStats(gameKey, stats));
        }
    }

    /**
     * Migrate local stats to server
     */
     async migrateStats() {
        for (let gameKey of GAME_KEYS) {
            if (!personalStatsLocal.hasStats(gameKey)) {
                continue;
            }
            const stats = personalStatsLocal.getStats(gameKey);
            await personsalStatsServer.setStats(gameKey, stats);
            personalStatsLocal.clear(gameKey);
        }
    }
    formatPersonalStats(stats: PersonalStatsState):FormattedStats {
        const formatTime = (timeInSeconds) => {
            if (timeInSeconds === null) {
                return 'N/A';
            }
            const hours = Math.floor(timeInSeconds / 3600);
            const minutes = Math.floor((timeInSeconds % 3600) / 60);
            const seconds = Math.round(timeInSeconds % 60);

            return `${padZero(hours)}:${padZero(minutes)}:${padZero(seconds)}`;   
        }

        let winPercentage = 'N/A';
        if (stats.games > 0) {
            winPercentage = (stats.wins / stats.games * 100).toFixed(1) + '%';
        }
       return {
              games: stats.games,
              wins: stats.wins,
              winPercentage: winPercentage,
              fastestTimeFormatted: formatTime(stats.fastestTime),
              averageTimeFormatted: formatTime(stats.averageTime)
       }
    }
}

export default PersonalStats;
